import React, { type FC, useState, useCallback, useEffect, useRef } from 'react';
import { Coordination } from '@atlassiansox/engagekit-ts';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { SpotlightTarget, Spotlight, SpotlightTransition } from '@atlaskit/onboarding';
import BoardIcon from '@atlaskit/icon/core/board';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { AdControlProvider } from '@atlassian/ad-control-toolkit';

import { LoadableAfterPaint } from '@confluence/loadable';
import { useRouteName } from '@confluence/route-manager';
import { Attribution, TransparentErrorBoundary, ErrorDisplay } from '@confluence/error-boundary';
import { SPACE_JIRA_PROJECT_BOARD } from '@confluence/named-routes';
import { useCoordinationClient } from '@confluence/engagement-provider';
import {
	confluenceLocalStorageInstance,
	PERSISTED_KEYS_ON_SERVER,
} from '@confluence/storage-manager';

import { useUserState } from '../hooks/useUserState';

import { AdMenu } from './AdMenu';

export const SPOTLIGHT_MESSAGE_ID = 'cc-jira-board-spotlight-v2-message-id';
export const SPOTLIGHT_TARGET_ID = 'jira-board-in-confluence-spotlight-id';

const DISMISSALS_STORAGE_KEY = PERSISTED_KEYS_ON_SERVER.PERSISTED_JIRA_AD_DISMISSAL;

type JiraBoardNavigationItemProps = {
	spaceKey: string;
	isNav4Enabled: boolean;
};

const messages = defineMessages({
	navigationItemLabel: {
		id: 'jira-project-board.navigation-item-label',
		defaultMessage: 'Jira project',
		description: 'Label for the Jira Board navigation item',
	},
	spotlightHeading: {
		id: 'jira-project-board.spotlight-heading',
		defaultMessage: 'Manage projects within Confluence',
		description: 'Heading for the Jira Board in Confluence spotlight',
	},
	spotlightDescription: {
		id: 'jira-project-board.spotlight-description',
		defaultMessage:
			'Link real-time data to any document, get automatic updates, and easily create reports.',
		description: 'Description for the Jira Board in Confluence spotlight',
	},
	spotlightTryNow: {
		id: 'jira-project-board.spotlight-try-now',
		defaultMessage: 'Try now',
		description: 'Label for the try now button in the Jira Board in Confluence spotlight',
	},
	spotlightDismiss: {
		id: 'jira-project-board.spotlight-dismiss',
		defaultMessage: 'Dismiss',
		description: 'Label for the dismiss button in the Jira Board in Confluence spotlight',
	},
});

const MenuItemComponent = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-MenuItemComponent" */ './MenuItemComponent'))
			.MenuItemComponent,
});

const MenuItemComponentNav3 = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-MenuItemComponentNav3" */ './MenuItemComponentNav3'
			)
		).MenuItemComponentNav3,
});

export const JiraBoardNavigationItemBase: FC<JiraBoardNavigationItemProps> = ({
	spaceKey,
	isNav4Enabled,
}) => {
	const { formatMessage } = useIntl();
	const [isSpotlightActive, setIsSpotlightActive] = useState(true);
	const coordinationClient = useCoordinationClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { hasJira, isSiteAdmin, isLoading, userStateDataError } = useUserState();
	const hasSpotlightScreenViewedEventBeenFired = useRef(false);

	const isOnJiraBoardRouteArgs = {
		selector: (routeName: string | undefined) => routeName === SPACE_JIRA_PROJECT_BOARD.name,
	};
	const isOnJiraBoardRoute = useRouteName(isOnJiraBoardRouteArgs);

	const [isAdDismissed, setIsAdDismissed] = useState<boolean>(
		confluenceLocalStorageInstance.getItemAsBoolean(DISMISSALS_STORAGE_KEY),
	);

	// The ad control dismissal is relevant only until they provision Jira
	const showAdMenu = !hasJira && !isLoading && !userStateDataError;

	const FireSpotlightScreenViewedEvent: FC = () => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		useEffect(() => {
			if (!hasSpotlightScreenViewedEventBeenFired.current) {
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						name: 'jiraBoardSpotlight',
						source: 'JiraBoardNavigationItemNudge',
					},
				}).fire();
				hasSpotlightScreenViewedEventBeenFired.current = true;
			}
		}, [createAnalyticsEvent]);

		return null;
	};

	const onDismiss = useCallback(async () => {
		setIsSpotlightActive(false);
		await coordinationClient.stop(SPOTLIGHT_MESSAGE_ID);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'jiraBoardSpotlightDismissed',
				source: 'JiraBoardNavigationItemNudge',
			},
		}).fire();
	}, [coordinationClient, createAnalyticsEvent]);

	const onTryNow = useCallback(async () => {
		setIsSpotlightActive(false);
		await coordinationClient.stop(SPOTLIGHT_MESSAGE_ID);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'jiraBoardSpotlightTryNow',
				source: 'JiraBoardNavigationItemNudge',
			},
		}).fire();
	}, [coordinationClient, createAnalyticsEvent]);

	const dismissAd = useCallback(async () => {
		confluenceLocalStorageInstance.setItem(DISMISSALS_STORAGE_KEY, true);
		setIsAdDismissed(true);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'notInterested',
				source: 'JiraBoardNavigationAdControl',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	// The ad control dismissal is relevant only until they provision Jira
	if (!hasJira && isAdDismissed) {
		return null;
	}

	return (
		<>
			{userStateDataError && <ErrorDisplay error={userStateDataError} />}
			<SpotlightTarget name={SPOTLIGHT_TARGET_ID}>
				{isNav4Enabled ? (
					<MenuItemComponent
						href={SPACE_JIRA_PROJECT_BOARD.toUrl({ spaceKey })}
						iconBefore={<BoardIcon spacing="spacious" label="" />}
						iconAfter={showAdMenu && <AdMenu onDismiss={dismissAd} isSiteAdmin={isSiteAdmin} />}
						isSelected={isOnJiraBoardRoute}
					>
						<FormattedMessage {...messages.navigationItemLabel} />
					</MenuItemComponent>
				) : (
					<MenuItemComponentNav3
						href={SPACE_JIRA_PROJECT_BOARD.toUrl({ spaceKey })}
						iconBefore={<BoardIcon spacing="spacious" label="" />}
						iconAfter={showAdMenu && <AdMenu onDismiss={dismissAd} isSiteAdmin={isSiteAdmin} />}
						isSelected={isOnJiraBoardRoute}
					>
						<FormattedMessage {...messages.navigationItemLabel} />
					</MenuItemComponentNav3>
				)}
			</SpotlightTarget>
			<SpotlightTransition>
				{isSpotlightActive && (
					<Coordination client={coordinationClient} messageId={SPOTLIGHT_MESSAGE_ID}>
						<>
							<FireSpotlightScreenViewedEvent />
							<Spotlight
								actions={[
									{
										href: SPACE_JIRA_PROJECT_BOARD.toUrl({ spaceKey }),
										onClick: async () => {
											await onTryNow();
										},
										text: formatMessage(messages.spotlightTryNow),
									},
									{
										onClick: onDismiss,
										text: formatMessage(messages.spotlightDismiss),
									},
								]}
								heading={formatMessage(messages.spotlightHeading)}
								target={SPOTLIGHT_TARGET_ID}
								key={SPOTLIGHT_TARGET_ID}
								targetRadius={3}
								dialogPlacement="right bottom"
								dialogWidth={275}
							>
								<FormattedMessage {...messages.spotlightDescription} />
							</Spotlight>
						</>
					</Coordination>
				)}
			</SpotlightTransition>
		</>
	);
};

export const JiraBoardNavigationItem: FC<JiraBoardNavigationItemProps> = (props) => {
	return (
		<TransparentErrorBoundary attribution={Attribution.GROWTH_TRAILBLAZERS}>
			<AdControlProvider>
				<JiraBoardNavigationItemBase {...props} />
			</AdControlProvider>
		</TransparentErrorBoundary>
	);
};
